
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Check, ChevronDown, ChevronUp } from 'lucide-react';

interface Option {
    value: string;
    label: string;
}

interface MultiSelectProps {
    label?: string;
    placeholder?: string;
    options: Option[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    error?: string;
    className?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
    label,
    placeholder = "Select options...",
    options,
    selectedValues,
    onChange,
    error,
    className = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const filteredOptions = useMemo(() => {
        return options.filter(opt =>
            opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            opt.value.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [options, searchQuery]);

    const toggleOption = (value: string) => {
        const newValues = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];
        onChange(newValues);
    };

    const removeValue = (e: React.MouseEvent, value: string) => {
        e.stopPropagation();
        onChange(selectedValues.filter(v => v !== value));
    };

    const selectedOptions = useMemo(() => {
        return options.filter(opt => selectedValues.includes(opt.value));
    }, [options, selectedValues]);

    return (
        <div className={`w-full space-y-1.5 ${className}`} ref={containerRef}>
            {label && <label className="text-xs font-bold text-brand-muted uppercase tracking-wider ml-1">{label}</label>}

            <div className="relative">
                <div
                    onClick={() => setIsOpen(!isOpen)}
                    className={`
            w-full bg-brand-dark/50 border min-h-[52px] rounded-xl px-4 py-2 flex flex-wrap gap-2 items-center cursor-pointer transition-all duration-300
            ${isOpen ? 'border-brand-primary ring-1 ring-brand-primary/20 bg-brand-primary/[0.02]' : 'border-brand-border'}
            ${error ? 'border-red-500/50' : ''}
          `}
                >
                    {selectedOptions.length === 0 ? (
                        <span className="text-brand-muted/40 text-sm font-bold ml-2">{placeholder}</span>
                    ) : (
                        selectedOptions.map(opt => (
                            <div
                                key={opt.value}
                                className="bg-brand-primary/10 border border-brand-primary/20 rounded-lg px-2 py-1 flex items-center gap-1 animate-in zoom-in-95 duration-200"
                            >
                                <span className="text-[10px] font-black text-brand-primary uppercase tracking-tight">{opt.label.split(' (')[0]}</span>
                                <button
                                    onClick={(e) => removeValue(e, opt.value)}
                                    className="p-0.5 hover:bg-brand-primary/20 rounded-md transition-colors"
                                >
                                    <X size={10} className="text-brand-primary" />
                                </button>
                            </div>
                        ))
                    )}

                    <div className="ml-auto text-brand-muted shrink-0 pl-2">
                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                </div>

                {isOpen && (
                    <div className="absolute top-full left-0 w-full mt-2 bg-brand-card border border-brand-border rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-[200] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="p-3 border-b border-brand-border bg-brand-dark/40 flex items-center gap-3">
                            <Search size={14} className="text-brand-muted" />
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="bg-transparent border-none outline-none text-xs text-white w-full font-bold"
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} className="p-1 hover:bg-brand-border rounded-lg transition-colors">
                                    <X size={12} className="text-brand-muted" />
                                </button>
                            )}
                        </div>

                        <div className="max-h-60 overflow-y-auto p-1 divide-y divide-brand-border/10">
                            {filteredOptions.length === 0 ? (
                                <div className="px-4 py-8 text-center text-brand-muted text-[10px] font-black uppercase tracking-widest opacity-50">
                                    No matching entities found
                                </div>
                            ) : (
                                filteredOptions.map(opt => {
                                    const isSelected = selectedValues.includes(opt.value);
                                    return (
                                        <div
                                            key={opt.value}
                                            onClick={() => toggleOption(opt.value)}
                                            className={`
                        flex items-center justify-between px-4 py-3 cursor-pointer transition-all group
                        ${isSelected ? 'bg-brand-primary/5' : 'hover:bg-white/5'}
                      `}
                                        >
                                            <div className="space-y-0.5">
                                                <p className={`text-xs font-bold transition-colors ${isSelected ? 'text-brand-primary' : 'text-white group-hover:text-brand-primary'}`}>{opt.label}</p>
                                                <p className="text-[9px] text-brand-muted font-mono opacity-60 uppercase">{opt.value}</p>
                                            </div>
                                            {isSelected && (
                                                <div className="w-5 h-5 rounded-lg bg-brand-primary flex items-center justify-center animate-in zoom-in duration-200">
                                                    <Check size={12} className="text-black stroke-[3px]" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {selectedValues.length > 0 && (
                            <div className="p-2 border-t border-brand-border bg-brand-dark/20 flex justify-between items-center px-4">
                                <span className="text-[9px] font-black text-brand-muted uppercase tracking-widest">{selectedValues.length} Selected</span>
                                <button
                                    onClick={() => onChange([])}
                                    className="text-[9px] font-black text-brand-primary uppercase tracking-widest hover:underline"
                                >
                                    Clear All
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {error && <p className="text-[10px] text-red-400 font-medium ml-1">{error}</p>}
        </div>
    );
};

export default MultiSelect;
